<template>
    <header class="flex items-center justify-between">
        <div class="flex items-center gap-2 sm:gap-4 md:gap-8">
            <prime-button
                type="button"
                class="js-TopBarMenuButton inline-flex shrink-0 items-center justify-center !rounded-full transition-colors duration-200 ease-in hover:bg-primary-600 max-md:!h-[44px] max-md:!w-[44px] md:!h-10 md:!w-10"
                :aria-label="t('layout.toggleNavigation')"
                link
                @click="onMenuToggle"
            >
                <i
                    class="pi pi-bars text-xl text-gray-500 transition-colors duration-200 hover:text-white"
                    aria-hidden="true"
                />
            </prime-button>

            <!-- Mobile page headings provide context without wrapping the navigation toolbar. -->
            <app-breadcrumb class="hidden md:block" />
        </div>

        <!-- Let long customer names truncate on mobile without displacing the language control. -->
        <div class="max-md:min-w-0">
            <ul class="flex list-none items-center md:gap-3">
                <li class="max-md:min-w-0">
                    <nuxt-link :to="profileLink()">
                        <prime-button
                            class="max-md:max-w-full max-md:[&_[data-pc-section=label]]:truncate"
                            :label="selectedCustomer.firstName"
                            text
                            severity="secondary"
                            :icon="
                                selectedCustomer.id === viewer?.customerId
                                    ? 'pi pi-user'
                                    : 'pi pi-users'
                            "
                        />
                    </nuxt-link>
                </li>

                <li class="hidden md:block">
                    <prime-button
                        type="button"
                        :label="t('layout.signOut')"
                        icon="pi pi-sign-out"
                        text
                        severity="secondary"
                        :loading="isSigningOut"
                        @click="signOut"
                    />
                </li>

                <!-- Profile pages use the same far-right responsive language control as public headers. -->
                <li class="max-md:shrink-0"><header-locale-switcher /></li>
            </ul>
        </div>
    </header>
</template>

<script setup lang="ts">
import AppBreadcrumb from './AppBreadcrumb.vue'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useLayout } from './composables/layout'
import PrimeButton from 'primevue/button'
import { useSelectedCustomer } from '~/components/Layouts/ProfileLayout/composables/use-selected-customer'
import { useSignOut, useViewer } from '~/composables/use-login'
import { useRouteLink } from '~/composables/routing'
import NuxtLink from '#app/components/nuxt-link'
import { useI18n } from 'vue-i18n'
import HeaderLocaleSwitcher from '~/components/Layouts/HeaderLocaleSwitcher.vue'

const { profileLink } = useRouteLink()
const { onMenuToggle } = useLayout()
const { t } = useI18n({ useScope: 'global' })

const outsideClickListener = ref<((event: MouseEvent) => void) | null>(null)
const topbarMenuActive = ref(false)

onMounted(() => {
    bindOutsideClickListener()
})

onBeforeUnmount(() => {
    unbindOutsideClickListener()
})

const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
        outsideClickListener.value = event => {
            if (isOutsideClicked(event)) {
                topbarMenuActive.value = false
            }
        }
        document.addEventListener('click', outsideClickListener.value)
    }
}
function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        // Remove the registered callback so document clicks cannot outlive the topbar.
        document.removeEventListener('click', outsideClickListener.value)
        outsideClickListener.value = null
    }
}
const isOutsideClicked = event => {
    if (!topbarMenuActive.value) return

    const sidebarEl = document.querySelector('.layout-topbar-menu')
    const topbarEl = document.querySelector('.layout-topbar-menu-button')

    return !(
        sidebarEl.isSameNode(event.target) ||
        sidebarEl.contains(event.target) ||
        topbarEl.isSameNode(event.target) ||
        topbarEl.contains(event.target)
    )
}

const viewer = useViewer()
const selectedCustomer = useSelectedCustomer()

const { isSigningOut, signOut } = useSignOut()
</script>
